import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { jobOpenings } from '../data/mockData';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ChevronRight, Briefcase, MapPin, Clock, Check, CheckCircle, Upload } from 'lucide-react';
import SEO from '../components/SEO';

const Employment = () => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    resume: null,
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Application:', formData);
    setSubmitted(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-gray-50">
        <div className="text-center px-4">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-[#1e3a52] mb-4">Application Submitted!</h1>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Thank you for your interest in joining Bu Security. Our HR team will review your application and contact you soon.
          </p>
          <Link to="/">
            <Button className="bg-[#5ba4c8] hover:bg-[#4a93b7]">
              Return to Home
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title="Employment Opportunities - Security Guard Jobs in Miami, Florida"
        description="Join Bu Security team! We're hiring armed security officers, unarmed security guards, mobile patrol officers, and event security specialists in Miami, Florida. Competitive pay and benefits."
        keywords="security guard jobs Miami, security officer jobs Florida, armed guard jobs, unarmed guard jobs, security employment, security careers Miami, security guard hiring"
      />
      {/* Hero Section */}
      <section className="relative h-[350px] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1920&h=600&fit=crop')`,
          }}
        />
        <div className="absolute inset-0 bg-[#1e3a52]/80" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 w-full">
          <div className="flex items-center gap-2 text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-[#5ba4c8]">Home</Link>
            <ChevronRight size={16} />
            <span className="text-[#5ba4c8]">Employment Opportunity</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-wider">
            Join Our Team
          </h1>
          <p className="text-gray-300 mt-4 max-w-2xl">
            Build a rewarding career in professional security services
          </p>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#1e3a52] mb-12">
            Why Work With Bu Security?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Competitive Pay',
                description: 'We offer competitive wages and benefits packages for all our team members.'
              },
              {
                title: 'Professional Growth',
                description: 'Ongoing training programs and advancement opportunities within the company.'
              },
              {
                title: 'Flexible Schedules',
                description: 'Various shift options available to fit your lifestyle and commitments.'
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-[#5ba4c8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-[#5ba4c8]" />
                </div>
                <h3 className="text-xl font-bold text-[#1e3a52] mb-2">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Job Listings */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-[#1e3a52] mb-12">
            Current Openings
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {jobOpenings.map((job) => (
              <div 
                key={job.id} 
                className={`bg-white rounded-lg shadow-md p-6 cursor-pointer transition-all hover:shadow-lg ${
                  selectedJob === job.id ? 'ring-2 ring-[#5ba4c8]' : ''
                }`}
                onClick={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-[#1e3a52]">{job.title}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin size={14} /> {job.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={14} /> {job.type}
                      </span>
                    </div>
                  </div>
                  <div className="w-10 h-10 bg-[#5ba4c8]/10 rounded-full flex items-center justify-center">
                    <Briefcase size={20} className="text-[#5ba4c8]" />
                  </div>
                </div>
                
                {selectedJob === job.id && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h4 className="font-medium text-[#1e3a52] mb-2">Requirements:</h4>
                    <ul className="space-y-2">
                      {job.requirements.map((req, index) => (
                        <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                          <Check size={14} className="text-[#5ba4c8]" />
                          {req}
                        </li>
                      ))}
                    </ul>
                    <Button 
                      className="mt-4 bg-[#5ba4c8] hover:bg-[#4a93b7] text-white"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFormData({ ...formData, position: job.title });
                        document.getElementById('application-form').scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      Apply Now
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section id="application-form" className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gray-50 rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-[#1e3a52] mb-6">Apply Now</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="firstName" className="text-[#1e3a52]">First Name *</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName" className="text-[#1e3a52]">Last Name *</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="email" className="text-[#1e3a52]">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-[#1e3a52]">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="position" className="text-[#1e3a52]">Position Applying For *</Label>
                  <Input
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Armed Security Officer"
                    className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                  />
                </div>
                <div>
                  <Label htmlFor="experience" className="text-[#1e3a52]">Years of Experience</Label>
                  <Input
                    id="experience"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    placeholder="e.g., 3 years"
                    className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                  />
                </div>
              </div>

              <div>
                <Label className="text-[#1e3a52]">Resume</Label>
                <div className="mt-1 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Upload size={32} className="text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Drag and drop your resume here, or click to browse</p>
                  <input type="file" className="hidden" accept=".pdf,.doc,.docx" />
                </div>
              </div>

              <div>
                <Label htmlFor="message" className="text-[#1e3a52]">Additional Information</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Tell us about yourself and why you want to join our team..."
                  className="mt-1 border-gray-300 focus:border-[#5ba4c8] focus:ring-[#5ba4c8]"
                />
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#5ba4c8] hover:bg-[#4a93b7] text-white py-3"
              >
                Submit Application
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Employment;
